import React from 'react';
import { Tooltip } from '@mui/material';
import type { DimensionScores } from './types';
import type { Reviewers } from '../../../../graphql';
import { PANEL_APPLICATION_REVIEWER_TEXT } from '../../../../lang';
import { FundingSource } from '../../../../../Nomination/components';
import { Dialog, TextField } from '../../../../../../components';
import { theme } from '../../../../../../Theme';

type ReviewDetailsValue = {
  fundingSource: FundingSource;
  reviewersScores: Reviewers[];
  scores: DimensionScores;
};

type ReviewDetailsDialogProps = {
  openDialog: boolean;
  dialogContent: ReviewDetailsValue;
  onDialogClose: () => void;
};

export const ReviewDetailsDialog: React.FC<ReviewDetailsDialogProps> = (
  props: ReviewDetailsDialogProps,
) => {
  const { openDialog, dialogContent, onDialogClose } = props;
  const { reviewersScores, fundingSource, scores } = dialogContent;

  const isHKPFS =
    fundingSource === FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME;
  const ratingMapping = [
    {
      label: PANEL_APPLICATION_REVIEWER_TEXT.AE,
      tooltip: PANEL_APPLICATION_REVIEWER_TEXT.AE,
      deptRating: scores.AE,
      reviewers: reviewersScores.map(reviewer => ({
        isHighlighted:
          Boolean(reviewer.scoresOverride.AE) &&
          String(reviewer.scoresOverride.AE) !== String(scores.AE),
        netId: reviewer.netId,
        name: reviewer.name,
        department: reviewer.department,
        score: reviewer.scoresOverride.AE,
        comment: reviewer.scoresOverride.AEComment,
      })),
    },
    {
      label: PANEL_APPLICATION_REVIEWER_TEXT.RP,
      deptRating: scores.RP,
      tooltip: PANEL_APPLICATION_REVIEWER_TEXT.RP_TOOLTIP,
      reviewers: reviewersScores.map(reviewer => ({
        isHighlighted:
          Boolean(reviewer.scoresOverride.RP) &&
          String(reviewer.scoresOverride.RP) !== String(scores.RP),
        netId: reviewer.netId,
        name: reviewer.name,
        department: reviewer.department,
        score: reviewer.scoresOverride.RP,
        comment: reviewer.scoresOverride.RPComment,
      })),
    },
    {
      label: PANEL_APPLICATION_REVIEWER_TEXT.LS,
      deptRating: scores.LS,
      tooltip: PANEL_APPLICATION_REVIEWER_TEXT.LS_TOOLTIP,
      reviewers: reviewersScores.map(reviewer => ({
        isHighlighted:
          Boolean(reviewer.scoresOverride.LS) &&
          String(reviewer.scoresOverride.LS) !== String(scores.LS),
        netId: reviewer.netId,
        name: reviewer.name,
        department: reviewer.department,
        score: reviewer.scoresOverride.LS,
        comment: reviewer.scoresOverride.LSComment,
      })),
    },
    {
      label: PANEL_APPLICATION_REVIEWER_TEXT.CI,
      deptRating: scores.CI,
      tooltip: PANEL_APPLICATION_REVIEWER_TEXT.CI_TOOLTIP,
      reviewers: reviewersScores.map(reviewer => ({
        isHighlighted:
          Boolean(reviewer.scoresOverride.CI) &&
          String(reviewer.scoresOverride.CI) !== String(scores.CI),
        netId: reviewer.netId,
        name: reviewer.name,
        department: reviewer.department,
        score: reviewer.scoresOverride.CI,
        comment: reviewer.scoresOverride.CIComment,
      })),
    },
    ...(isHKPFS
      ? []
      : [
          {
            label: PANEL_APPLICATION_REVIEWER_TEXT.RE,
            deptRating: scores.RE,
            tooltip: PANEL_APPLICATION_REVIEWER_TEXT.RE,
            reviewers: reviewersScores.map(reviewer => ({
              isHighlighted:
                Boolean(reviewer.scoresOverride.RE) &&
                String(reviewer.scoresOverride.RE) !== String(scores.RE),
              netId: reviewer.netId,
              name: reviewer.name,
              department: reviewer.department,
              score: reviewer.scoresOverride.RE,
              comment: reviewer.scoresOverride.REComment,
            })),
          },
          {
            label: PANEL_APPLICATION_REVIEWER_TEXT.OS,
            deptRating: scores.OS,
            tooltip: PANEL_APPLICATION_REVIEWER_TEXT.OS,
            reviewers: reviewersScores.map(reviewer => ({
              isHighlighted:
                Boolean(reviewer.scoresOverride.OS) &&
                String(reviewer.scoresOverride.OS) !== String(scores.OS),
              netId: reviewer.netId,
              name: reviewer.name,
              department: reviewer.department,
              score: reviewer.scoresOverride.OS,
              comment: reviewer.scoresOverride.OSComment,
            })),
          },
        ]),
    {
      label: PANEL_APPLICATION_REVIEWER_TEXT.CD,
      deptRating: scores.CD,
      tooltip: PANEL_APPLICATION_REVIEWER_TEXT.CD,
      reviewers: reviewersScores.map(reviewer => ({
        isHighlighted:
          Boolean(reviewer.scoresOverride.CD) &&
          String(reviewer.scoresOverride.CD) !== String(scores.CD),
        netId: reviewer.netId,
        name: reviewer.name,
        department: reviewer.department,
        score: reviewer.scoresOverride.CD,
        comment: reviewer.scoresOverride.CDComment,
      })),
    },
    {
      label: PANEL_APPLICATION_REVIEWER_TEXT.US,
      deptRating: scores.US,
      tooltip: PANEL_APPLICATION_REVIEWER_TEXT.US,
      reviewers: reviewersScores.map(reviewer => ({
        isHighlighted:
          Boolean(reviewer.scoresOverride.US) &&
          String(reviewer.scoresOverride.US) !== String(scores.US),
        netId: reviewer.netId,
        name: reviewer.name,
        department: reviewer.department,
        score: reviewer.scoresOverride.US,
        comment: reviewer.scoresOverride.USComment,
      })),
    },
  ];

  return (
    <>
      <Dialog
        dialogTitle={PANEL_APPLICATION_REVIEWER_TEXT.REVIEW_DETAILS}
        openDialog={openDialog}
        onDialogClose={onDialogClose}
        displayCloseButton
        scroll="body"
        width="65rem"
      >
        <div className="grid grid-cols-2 px-6">
          {ratingMapping.map((rating, index) => {
            const { label, reviewers, tooltip } = rating;
            return (
              <div key={index} className="mb-2 mx-2">
                <div
                  className="flex flex-row space-between grid grid-cols-4 gap-2 items-center mb-3"
                  style={{ gridTemplateColumns: '6fr 4fr 2fr 5fr' }}
                >
                  <Tooltip title={tooltip} arrow>
                    <span
                      className={`text-[${theme.palette.primary.main}] font-medium `}
                    >
                      {label}
                    </span>
                  </Tooltip>
                  <span className="flex flex-row justify-center items-center font-medium">
                    {PANEL_APPLICATION_REVIEWER_TEXT.DEPT_RATING}
                  </span>
                  <TextField size="small" disabled value={rating.deptRating} />
                  <span />
                </div>
                <div>
                  {reviewers.map((reviewer, reviewerIndex) => {
                    const { name, department, score, comment, isHighlighted } =
                      reviewer;
                    return (
                      <div
                        key={reviewerIndex}
                        className="grid grid-cols-4 gap-2 mb-2 place-items-start"
                        style={{ gridTemplateColumns: '5fr 5fr 2fr 5fr' }}
                      >
                        <span className="font-medium text-start">{`${name} [${department}]`}</span>
                        <span className="text-center">
                          {PANEL_APPLICATION_REVIEWER_TEXT.AMENDED_RATING}
                        </span>
                        <TextField
                          size="small"
                          disabled
                          value={score}
                          sx={{
                            backgroundColor: isHighlighted
                              ? '#F3E6E6'
                              : 'inherit',
                          }}
                        />
                        <TextField
                          size="small"
                          multiline
                          disabled
                          value={comment}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Dialog>
    </>
  );
};
